import { put, takeLatest } from "@redux-saga/core/effects";
import {
  CoreJsonResponse,
  genFetch,
  IRequestObject,
  RelationshipsEnums,
  reqHandler,
  RequestType,
  withToken,
} from "coral-utility";
import {
  GET_TAG_OPTIONS,
  TagOptions,
  TagTypeOption,
  InstrumentTypeOption,
  ProgramVersionTagOption,
} from "./types";
import { authenticatedFailureAction } from "../connect/actions";
import { tags } from "state/api/tags";
import { setTagOptions, setTagLoading } from "./actions";
import { stringSorter } from "hooks/helpers/sorters";
import { TagTree } from "types";

function* getTagOptions() {
  try {
    yield put(setTagLoading(true));
    const tagTreeRequest: IRequestObject = {
      onFetch: () => genFetch(tags.getAllTrees())(withToken())(RelationshipsEnums.INCLUDE_RELATIONS),
      onSuccess: (response: CoreJsonResponse<TagTree[], {}>) => {
        let tagOptions: TagOptions = {
          instrumentTypeOptions: [],
          modelTagOptions: [],
          analysisTagOptions: [],
          tagOptions: [],
          allTagOptions: [],
          programVersionOptions: [],
        };
        const tagParent = response.data.filter(
          (options) => options.name === "Tags"
        );
        if (tagParent[0]) {
          const newTagOptions: TagTypeOption[] = tagParent[0].branches.map(
            (branch) => ({
              id: branch.tag.name,
              tag: branch.tag.id,
              modelTags: branch.tag.id,
              analysisTag: branch.tag.id,
              instrumentType: branch.tag.id,
            })
          );
          newTagOptions.sort((a, b) => stringSorter(a, b, "id"));
          tagOptions.tagOptions = newTagOptions;
        }
        const modelTagParent = response.data.filter(
          (options) => options.name === "Model Tags"
        );
        if (modelTagParent[0]) {
          const newModelTagOptions: TagTypeOption[] = modelTagParent[0].branches.map(
            (branch) => ({
              id: branch.tag.name,
              tag: branch.tag.id,
              modelTags: branch.tag.id,
              analysisTag: branch.tag.id,
              instrumentType: branch.tag.id,
            })
          );
          newModelTagOptions.sort((a, b) => stringSorter(a, b, "id"));
          tagOptions.modelTagOptions = newModelTagOptions;
        }
        const analysisTagParent = response.data.filter(
          (options) => options.name === "Analysis Tags"
        );
        if (analysisTagParent[0]) {
          const newAnalysisTagOptions: TagTypeOption[] = analysisTagParent[0].branches.map(
            (branch) => ({
              id: branch.tag.name,
              tag: branch.tag.id,
              modelTags: branch.tag.id,
              analysisTag: branch.tag.id,
              instrumentType: branch.tag.id,
            })
          );
          newAnalysisTagOptions.sort((a, b) => stringSorter(a, b, "id"));
          tagOptions.analysisTagOptions = newAnalysisTagOptions;
        }
        const modelTypeParent = response.data.filter(
          (options) => options.name === "Model Type"
        );
        if (modelTypeParent[0]) {
          const newModelTypeOptions: InstrumentTypeOption[] = modelTypeParent[0].branches.map(
            (branch) => ({
              id: branch.tag.name,
              tag: branch.tag.name,
              instrumentType: branch.tag.name,
            })
          );
          newModelTypeOptions.sort((a, b) => stringSorter(a, b, "id"));
          tagOptions.instrumentTypeOptions = newModelTypeOptions;
        }
        const programVersionParent = response.data.filter(
          (options) => options.name === "Program and version"
        );
        if (programVersionParent[0]) {
          const newProgramVersionOptions: ProgramVersionTagOption[] = programVersionParent[0].branches.map(
            (branch) => ({
              id: branch.tag.name,
              tag: branch.tag.name,
              programVersion: branch.tag.name,
            })
          );
          newProgramVersionOptions.sort((a, b) => stringSorter(a, b, "id"));
          tagOptions.programVersionOptions = newProgramVersionOptions;
        }

        function addUniqueOptions(
          originalOptions: TagTypeOption[],
          newOptions: TagTypeOption[]
        ) {
          const returnOptions = [...originalOptions];
          const existingIds = originalOptions.map((option) => option.tag);
          newOptions.forEach((option) => {
            if (!existingIds.includes(option.tag)) {
              newOptions.push(option);
            }
          });
          return returnOptions;
        }

        let newAllTagOptions = addUniqueOptions(
          tagOptions.tagOptions,
          tagOptions.modelTagOptions
        );
        newAllTagOptions = addUniqueOptions(
          newAllTagOptions,
          tagOptions.analysisTagOptions
        );
        newAllTagOptions.sort((a, b) => stringSorter(a, b, "id"));
        tagOptions.allTagOptions = newAllTagOptions;

        return put(setTagOptions(tagOptions));
      },
      onFailure: () => put(authenticatedFailureAction()),
      onDeserialize: "json",
      type: RequestType.ONE,
    };
    yield reqHandler(tagTreeRequest);
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setTagLoading(false));
  }
}

export default function* connectSaga() {
  yield takeLatest(GET_TAG_OPTIONS, getTagOptions);
}
